// src/components/modals/UpdateGoalModal.tsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getSupabase, currentUser } from "@/lib/supabase";

const sports = [
  "Running", "Cycling", "Swimming", "Triathlon", "CrossFit", "Hyrox",
  "Functional Fitness", "Football", "Rugby", "Tennis", "Gym", "Rowing"
];

const activityLevels = [
  "Sedentary",
  "Lightly active",
  "Moderately active",
  "Very active",
  "Extremely active"
];

interface UpdateGoalModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function UpdateGoalModal({ open, onClose, onSuccess }: UpdateGoalModalProps) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    goalType: "",
    description: "",
    targetWeightKg: "",
    targetDate: "",
    primarySport: "",
    secondarySport: "",
    activityLevel: "",
  });

  // Load current data
  useEffect(() => {
    if (!open) return;

    (async () => {
      const user = await currentUser();
      if (!user) return;

      // Load goal
      const { data: goal } = await getSupabase()
        .from("client_goal_logs")
        .select("goal_type, client_goal_description, client_weight_goal_in_kg, target_date")
        .eq("user_id", user.id)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Load profile data
      const { data: client } = await getSupabase()
        .from("clients")
        .select("primary_sport, secondary_sport, activity_level")
        .eq("user_id", user.id)
        .single();

      setForm({
        goalType: goal?.goal_type || "",
        description: goal?.client_goal_description || "",
        targetWeightKg: goal?.client_weight_goal_in_kg?.toString() || "",
        targetDate: goal?.target_date ? new Date(goal.target_date).toISOString().split("T")[0] : "",
        primarySport: client?.primary_sport || "",
        secondarySport: client?.secondary_sport || "",
        activityLevel: client?.activity_level || "",
      });
    })();
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in");

      const { data: { session } } = await getSupabase().auth.getSession();
      if (!session) throw new Error("No session");

      // 1. Update goal via edge function
      const goalRes = await fetch(
        "https://usbamprqshmoalvlkjch.supabase.co/functions/v1/existing-client-goal-create",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            goal_type: form.goalType,
            client_goal_description: form.description || undefined,
            client_weight_goal_in_kg: form.targetWeightKg ? Number(form.targetWeightKg) : undefined,
            target_date: form.targetDate || undefined,
          }),
        }
      );

      if (!goalRes.ok) {
        const err = await goalRes.json();
        throw new Error(err.error || "Failed to update goal");
      }

      // 2. Update sports + activity level in clients table
      const updateData: any = {};
      if (form.primarySport) updateData.primary_sport = form.primarySport;
      if (form.secondarySport) updateData.secondary_sport = form.secondarySport;
      if (form.activityLevel) updateData.activity_level = form.activityLevel;

      if (Object.keys(updateData).length > 0) {
        await getSupabase()
          .from("clients")
          .update(updateData)
          .eq("user_id", user.id);
      }

      onSuccess?.();
      onClose();
      alert("Goal, sports & activity level updated successfully!");
    } catch (err: any) {
      console.error(err);
      alert("Update failed: " + (err.message || "Please try again"));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
   <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-0 md:p-4 overflow-y-auto">
      <form 
  onSubmit={handleSubmit} 
  className="bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] border-t md:border border-[#e2e8f0] rounded-t-2xl md:rounded-2xl w-full max-w-2xl p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto shadow-2xl"
>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#0f172a]">Update Goal & Profile</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Adjust your targets and preferences</p>
          </div>
          <button 
            type="button" 
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-gray-600" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Goal Section */}
          <div className="space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-green-400 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-sm">1</div>
              Your Goal
            </h3>

            {/* Goal Type */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Goal Type *</label>
              <select
                required
                value={form.goalType}
                onChange={(e) => setForm({ ...form, goalType: e.target.value })}
                className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
              >
                <option value="">Choose your goal</option>
                <option value="Lose Weight">💪 Weight Loss</option>
                <option value="Gain Muscle">🏋️ Gain Muscle</option>
                <option value="Maintain Weight">⚖️ Maintain Weight</option>
                <option value="Improve Performance">🚀 Improve Performance</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Description <span className="text-gray-500">(optional)</span></label>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] placeholder-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
                placeholder="e.g. London Marathon 2026"
              />
            </div>

            {/* Target Weight & Date in Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Target Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={form.targetWeightKg}
                  onChange={(e) => setForm({ ...form, targetWeightKg: e.target.value })}
                  className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] placeholder-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
                  placeholder="75.0"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Target Date <span className="text-gray-500">(optional)</span></label>
                <input
                  type="date"
                  value={form.targetDate}
                  onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
                  className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#e2e8f0] my-6"></div>

          {/* Sports & Activity Section */}
          <div className="space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-green-400 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-sm">2</div>
              Sports & Activity
            </h3>

            {/* Primary Sport */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">Primary Sport</label>
              <select
                value={form.primarySport}
                onChange={(e) => setForm({ ...form, primarySport: e.target.value })}
                className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
              >
                <option value="">Select primary sport</option>
                {sports.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Secondary Sport & Activity Level in Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Secondary Sport <span className="text-gray-500">(optional)</span></label>
                <select
                  value={form.secondarySport}
                  onChange={(e) => setForm({ ...form, secondarySport: e.target.value })}
                  className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
                >
                  <option value="">None</option>
                  {sports.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Activity Level</label>
                <select
                  value={form.activityLevel}
                  onChange={(e) => setForm({ ...form, activityLevel: e.target.value })}
                  className="w-full bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-4 py-3 text-[#0f172a] focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition"
                >
                  <option value="">Select activity level</option>
                  {activityLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">How often do you train per week?</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-800 hover:bg-gray-700 py-3 sm:py-3.5 rounded-xl text-[#0f172a] font-medium transition text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !form.goalType}
            className="flex-1 bg-green-500 hover:bg-green-400 text-[#0f172a] py-3 sm:py-3.5 rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition text-sm sm:text-base shadow-lg shadow-green-500/20"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              "Update Everything"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
